import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  PlusOne,
} from "@mui/icons-material";
import Cached from "@mui/icons-material/Cached";
import FilterListIcon from "@mui/icons-material/FilterList";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {
  Box,
  Chip,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CreateVariableRequest,
  UpdateVariableRequest,
  VariableResponse,
} from "../../types/model/variable";
import VariableForm from "./VariableForm";

interface RowProps {
  row: VariableResponse;
  open: boolean;
  onToggle: () => void;
  onUpdate: (
    variableId: number,
    values: UpdateVariableRequest
  ) => Promise<void>;
  onToggleActive: (variableId: number) => Promise<void>;
}

const Row = ({ row, open, onToggle, onUpdate, onToggleActive }: RowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <React.Fragment>
      <TableRow onClick={onToggle} sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell component="th" scope="row">
          {row.name}
        </TableCell>
        <TableCell>{row.unit}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {row.categories.map((category) => (
              <Chip key={category.id} label={t(category.i18nLabel)} size="small" />
            ))}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={row.active ? t("generic.deactivate") : t("generic.activate")}>
            <IconButton onClick={(e) => { e.stopPropagation(); onToggleActive(row.id); }}>
                {row.active ? (
                    <ToggleOnIcon sx={{ color: "success.main", fontSize: 28 }} />
                ) : (
                    <ToggleOffIcon sx={{ color: "grey.600", fontSize: 28 }} />
                )}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <VariableForm
                variableData={row}
                onSubmit={(values) => onUpdate(row.id, values)}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface VariablesTableProps {
  variablesData: VariableResponse[];
  onCreate: (values: CreateVariableRequest) => Promise<void>;
  onUpdate: (
    variableId: number,
    values: UpdateVariableRequest
  ) => Promise<void>;
  onToggleActive: (variableId: number) => Promise<void>;
  onRefresh: () => void;
}

export default function VariablesTable({
  variablesData,
  onCreate,
  onUpdate,
  onToggleActive,
  onRefresh,
}: Readonly<VariablesTableProps>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState("");
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const filteredData = variablesData.filter((v) =>
    v.name.toLowerCase().includes(filter.toLowerCase())
  );

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("admin.variables.title")}
        </Typography>
        
        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("generic.refresh")}>
          <IconButton onClick={onRefresh}>
            <Cached />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("admin.variables.add-new")}>
          <IconButton onClick={() => setOpenRowId(openRowId === 'new' ? null : 'new')}>
            <PlusOne />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {showFilters && (
        <Grid2 container spacing={3} sx={{ p:2 }}>
          <Grid2 size={12}>
            <TextField
              name="name"
              label={t("generic.search")}
              value={filter}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer>
        <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%" }} />
              <TableCell>{t("admin.variables.name")}</TableCell>
              <TableCell sx={{ width: "15%" }}>
                {t("admin.variables.unit")}
              </TableCell>
              <TableCell>{t("admin.variables.categories")}</TableCell>
              <TableCell sx={{ width: "10%" }} align="right">
                {t("generic.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {openRowId === 'new' && (
               <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                  <Collapse in={true} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 2 }}>
                       <VariableForm
                          onSubmit={async (values) => {
                            await onCreate(values as CreateVariableRequest);
                            setOpenRowId(null);
                          }}
                          onCancel={() => setOpenRowId(null)}
                        />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((row) => (
              <Row
                key={row.id}
                row={row}
                open={openRowId === row.id}
                onToggle={() => setOpenRowId(openRowId === row.id ? null : row.id)}
                onUpdate={onUpdate}
                onToggleActive={onToggleActive}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}